import Base from "./../base";
import { InquiryAnswer, DispInquiryAnswer } from "./inquiryAnswer";
import { DefaultStaff, Staff } from "#/model/staff";
import { FileIF } from "./file";
import { Agreement, DEFAULT_AGREEMENT } from "@/model/agreement";
import { DEFAULT_OFFICE, Office } from "@/model/office";

export interface Inquiry extends Base {
  /** ID */ id: number;
  /** 問い合わせステータス 1:未対応 2:連絡とれず 3:進行中 9:対応完了 */ status: number;
  /** 公開フラグ 0:非公開 1:公開 */ is_published: number;
  /** 契約ID */ agreement_id: number;
  /** 事業所ID */ office_id: number;
  /** 問い合わせ者ID */ staff_id: number;
  /** 問い合わせ者名前 */ staff_name: string;
  /** 問い合わせ者職員権限 */ staff_auth_id: number;
  /** 問い合わせ日時 */ inquiry_datetime: string;
  /** 問い合わせ日 */ inquiry_date: string;
  /** 問い合わせ時刻 */ inquiry_time: string;
  /** 希望連絡日時 */ hope_contact_datetime: string;
  /** 問い合わせ方法 1:電話 2:iBowフォーム 3:営業経由 4:その他 */ inquiry_div: number;
  /** 希望連絡方法 0:— 1:どちらでも 2:電話 3:メール */ hope_contact_div: number;
  /** 電話番号 */ tel_no: string;
  /** 回答完了日時 */ complete_datetime: string;
  /** 問い合わせ内容 */ contact: string;
  /** 未読フラグ */ is_not_reading: number;
  /** 最終更新者ID */ admin_id: number;
  /** バージョン 0:iBow2 1:iBow */ version: number;
  /** 登録状況の確認希望 0:未選択 1:希望しない 2:希望する */ is_access_confirmed: number;
  /** 問い合わせタグID */ inquiry_tag_id: number;
  /** 問い合わせタグ名称 */ inquiry_tag_name: string;
  /** 問い合わせ日時（和暦） */ inquiry_datetime_wareki: string;
  /** 希望連絡日時（和暦） */ hope_contact_datetime_wareki: string;
  /** 回答完了日時（和暦） */ complete_datetime_wareki: string;
  /** 問い合わせに対する回答集 */ inquiry_answers: InquiryAnswer[];
  /** コンタクト情報 */ inquiry_contacts: InquiryContact[];
  /** 添付ファイル情報 */ inquiry_files: InquiryFile[];
  /** 親問い合わせID */ parent_inquiry_id: number;
  /** 子問い合わせ情報 */ sub_inquiries: Inquiry[];
  /** 問い合わせ日時(時分まで) */ inquiry_datetime_short: string;
  /** 希望連絡日時(時分まで) */ hope_contact_datetime_short: string;
}

/* 問い合わせ - コンタクト履歴 */
export interface InquiryContact extends Base {
  /** ID */ id: number;
  /** 問い合わせID */ inquiry_id: number;
  /** コンタクト内容 */ contact_info: string;
}

export interface InquiryFile extends Base, FileIF {
  /** ID */ id: number;
  /** 問い合わせID */ inquiry_id: number;
  /** 並び順 */ sort_no: number;
  /** ファイルパス */ file_path: string;
}

/** 表示用Inquiry */
export interface DispInquiry extends Inquiry {
  ticket_no: string;
  last_answered_datetime: string;
  first_response_datetime: string;
  co_code: string;
  kst_no: string;
  corresponding_admin_names: string;
  use_receipt_type: number;
  inquiry_date: string;
  inquiry_time: string;
  all_answer_contents: string;
  answer_statuses: number;
  divs: string;
  services: string;
  groups: string;
  admin_name: string;
  assignable: boolean;
  can_release: boolean;
  inquiry_answers: DispInquiryAnswer[];
  inquiry_agreement: Agreement;
  inquiry_office: Office;
  inquiry_staff: Staff;
}

/* 問い合わせ区分 */
export interface InquiryDiv extends Base {
  /** ID */ id: number;
  /** 並び順 */ sort_no: number;
  /** 区分名 */ name: string;
}

/* 問い合わせサービス種類 */
export interface InquiryServiceDiv extends Base {
  /** ID */ id: number;
  /** 並び順 */ sort_no: number;
  /** サービス種類名 */ name: string;
  /** 最初に開いているかどうか */ is_open: number;
  /** 問い合わせグループ */ inquiry_groups: InquiryGroup[];
}

/* 問い合わせグループ */
export interface InquiryGroup extends Base {
  /** ID */ id: number;
  /** 並び順 */ sort_no: number;
  /** グループ名 */ name: string;
  /** 問い合わせサービス種類ID */ service_div_id: number;
  /** 問い合わせサブグループ */ inquiry_sub_groups: InquirySubGroup[];
}

/* 問い合わせサブグループ */
export interface InquirySubGroup extends Base {
  /** ID */ id: number;
  /** 並び順 */ sort_no: number;
  /** サブグループ名 */ name: string;
  /** 問い合わせグループID */ inquiry_group_id: number;
}

/** 問い合わせ職員 */
export interface InquiryOfficeStaff extends Staff {
  agreement_name: string;
}

export const DEFAULT_INQUIRY: Inquiry = {
  id: 0,
  status: 1,
  is_published: 0,
  agreement_id: 0,
  office_id: 0,
  staff_id: 0,
  staff_name: "",
  staff_auth_id: 0,
  inquiry_datetime: "",
  inquiry_date: "",
  inquiry_time: "",
  hope_contact_datetime: "",
  inquiry_div: 4,
  hope_contact_div: 1,
  tel_no: "",
  complete_datetime: "",
  contact: "",
  admin_id: 0,
  version: 1,
  is_access_confirmed: 0,
  inquiry_tag_id: 0,
  inquiry_tag_name: "",
  is_not_reading: 0,
  inquiry_datetime_wareki: "",
  hope_contact_datetime_wareki: "",
  complete_datetime_wareki: "",
  inquiry_answers: [],
  inquiry_contacts: [],
  inquiry_files: [],
  parent_inquiry_id: 0,
  sub_inquiries: [],
  created_at: "",
  updated_at: "",
  deleted_at: "",
  inquiry_datetime_short: "",
  hope_contact_datetime_short: "",
};

export const DEFAULT_INQUIRY_CONTACT: InquiryContact = {
  id: 0,
  inquiry_id: 0,
  contact_info: "",
  created_at: "",
  updated_at: "",
  deleted_at: "",
};

export const DEFAULT_INQUIRY_FILE: InquiryFile = {
  id: 0,
  inquiry_id: 0,
  sort_no: 1,
  file_path: "",
  path: "",
  comment: "",
  created_at: "",
  updated_at: "",
  deleted_at: "",
};

export const DEFAULT_DISP_INQUIRY: DispInquiry = {
  ...DEFAULT_INQUIRY,
  ticket_no: "",
  last_answered_datetime: "",
  first_response_datetime: "",
  co_code: "",
  kst_no: "",
  corresponding_admin_names: "",
  use_receipt_type: 0,
  inquiry_date: "",
  inquiry_time: "",
  all_answer_contents: "",
  answer_statuses: 0,
  divs: "",
  services: "",
  groups: "",
  admin_name: "",
  assignable: false,
  can_release: false,
  inquiry_answers: [],
  inquiry_agreement: { ...DEFAULT_AGREEMENT },
  inquiry_office: { ...DEFAULT_OFFICE },
  inquiry_staff: { ...DefaultStaff() },
};

export const DEFAULT_INQUIRY_DIV: InquiryDiv = {
  id: 0,
  sort_no: 0,
  name: "",
  created_at: "",
  updated_at: "",
  deleted_at: "",
};

export const DEFAULT_INQUIRY_SERVICE_DIV: InquiryServiceDiv = {
  id: 0,
  sort_no: 0,
  name: "",
  is_open: 0,
  inquiry_groups: [],
  created_at: "",
  updated_at: "",
  deleted_at: "",
};

export const DEFAULT_INQUIRY_GROUP: InquiryGroup = {
  id: 0,
  sort_no: 0,
  name: "",
  service_div_id: 0,
  inquiry_sub_groups: [],
  created_at: "",
  updated_at: "",
  deleted_at: "",
};

export const DEFAULT_INQUIRY_SUB_GROUP: InquirySubGroup = {
  id: 0,
  sort_no: 0,
  name: "",
  inquiry_group_id: 0,
  created_at: "",
  updated_at: "",
  deleted_at: "",
};
